import type { Role } from '@compass/client/types/role';
import { useMemo } from 'react';

import { useOrgAndPrograms } from './useOrgAndPrograms';
import { useOrganizationRoles } from './useOrganizationRoles';
import { useProgramRoles } from './useProgramRoles';

/**
 * Hook to fetch and merge all available roles in an organization.
 *
 * Combines:
 * - Organization-level roles (roles with allowed_scope="organization")
 * - Program-level roles (roles with allowed_scope="program")
 *
 * Returns a flat, deduplicated list of all roles that can be assigned
 * within the current organization and program context.
 *
 * Automatically extracts organization_id and program_id from the global store.
 *
 * @returns {Object} Object containing:
 *   - roles: Merged array of Role objects from both organization and program
 *   - isLoading: True if either query is loading
 *   - isError: True if either query has an error
 *   - organizationRoles: Array of organization-level roles only
 *   - programRoles: Array of program-level roles only
 */
export const useAllOrganizationRoles = () => {
  // Get current organization and program IDs from store
  const { selectedOrganizationId } = useOrgAndPrograms();

  // Fetch organization roles
  const {
    data: orgRolesData,
    isLoading: isLoadingOrgRoles,
    isError: isErrorOrgRoles,
  } = useOrganizationRoles(selectedOrganizationId || undefined, { limit: 100 });

  // Fetch program roles for the selected program
  const {
    data: programRolesData,
    isLoading: isLoadingProgramRoles,
    isError: isErrorProgramRoles,
  } = useProgramRoles({ limit: 100 });

  // Extract role arrays with memoization
  const organizationRoles = useMemo(() => orgRolesData?.roles || [], [orgRolesData?.roles]);
  const programRoles = useMemo(() => programRolesData?.roles || [], [programRolesData?.roles]);

  // Merge and deduplicate roles by ID
  const mergedRoles = useMemo(() => {
    const roleMap = new Map<string, Role>();

    // Add organization roles
    organizationRoles.forEach(role => {
      if (role.id) {
        roleMap.set(role.id, role);
      }
    });

    // Add program roles (will overwrite if same ID exists)
    programRoles.forEach(role => {
      if (role.id) {
        roleMap.set(role.id, role);
      }
    });

    // Convert map back to array
    return Array.from(roleMap.values());
  }, [organizationRoles, programRoles]);

  return {
    roles: mergedRoles,
    isLoading: isLoadingOrgRoles || isLoadingProgramRoles,
    isError: isErrorOrgRoles || isErrorProgramRoles,
    organizationRoles,
    programRoles,
  };
};
